import { useRef, useState } from 'react';
import type { TagEntity, ProjectEntity } from '../../../entities/task/model/task.types';
import type { TaskPriority } from '../../../shared/lib/datetime';
import { PRIORITY_ORDER, PRIORITY_UI_LABEL } from '../../../shared/lib/priority';
import { EMPTY_TASK_FILTERS, type TaskFilters } from '../model/task.types';
import { hasActiveFilters } from '../model/selectors';

export interface TaskFiltersBarProps {
  filters: TaskFilters;
  onChange: (filters: TaskFilters) => void;
  allTags: TagEntity[];
  allProjects: ProjectEntity[];
}

const PRIORITY_OPTIONS: { value: TaskPriority | 'all'; label: string }[] = [
  { value: 'all', label: 'Все приоритеты' },
  ...PRIORITY_ORDER.map((value) => ({ value, label: PRIORITY_UI_LABEL[value] })),
];

/** Фильтры списков: поиск, теги, проект, приоритет, «Скрыть выполненные», сброс. */
export function TaskFiltersBar({ filters, onChange, allTags, allProjects }: TaskFiltersBarProps) {
  const [tagsOpen, setTagsOpen] = useState(false);
  const [projectOpen, setProjectOpen] = useState(false);
  const [priorityOpen, setPriorityOpen] = useState(false);
  const projectRef = useRef<HTMLDivElement | null>(null);
  const priorityRef = useRef<HTMLDivElement | null>(null);
  const active = hasActiveFilters(filters);

  const selectedProject =
    filters.projectId === null
      ? null
      : (allProjects.find((project) => project.id === filters.projectId) ?? null);
  const projectLabel = selectedProject?.name ?? 'Все проекты';
  const projectActive = filters.projectId !== null;

  const selectedPriorityLabel =
    filters.priority === 'all'
      ? 'Все приоритеты'
      : (PRIORITY_UI_LABEL[filters.priority] ?? 'Все приоритеты');

  function toggleTag(tagId: string): void {
    if (filters.tagIds.includes(tagId)) {
      onChange({ ...filters, tagIds: filters.tagIds.filter((id) => id !== tagId) });
    } else {
      onChange({ ...filters, tagIds: [...filters.tagIds, tagId] });
    }
  }

  function menuClass(isOpen: boolean): string {
    return [
      'absolute left-0 top-full z-30 mt-1 max-h-64 w-52 overflow-y-auto rounded-xl border border-tg-border-soft bg-tg-surface p-1 shadow-panel',
      isOpen ? '' : 'hidden',
    ]
      .filter(Boolean)
      .join(' ');
  }

  function itemClass(isSelected: boolean): string {
    return `block w-full rounded-lg px-2.5 py-2 text-left text-xs ${
      isSelected
        ? 'bg-tg-accent-soft text-tg-accent'
        : 'text-tg-text-secondary hover:bg-tg-surface-hover hover:text-tg-text'
    }`;
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          className="tg-input h-9 min-h-9 w-full sm:w-56"
          value={filters.search}
          placeholder="Поиск по названию…"
          aria-label="Поиск по названию задачи"
          onChange={(event) => {
            onChange({ ...filters, search: event.target.value });
          }}
        />

        <button
          type="button"
          className={`rounded-full border px-2.5 py-1 text-xs ${
            tagsOpen || filters.tagIds.length > 0
              ? 'border-tg-accent/50 bg-tg-accent-soft text-tg-accent'
              : 'border-tg-border-soft bg-tg-surface text-tg-text-secondary'
          }`}
          onClick={() => {
            setTagsOpen((value) => !value);
            setProjectOpen(false);
            setPriorityOpen(false);
          }}
          aria-expanded={tagsOpen}
        >
          Теги{filters.tagIds.length > 0 ? ` (${filters.tagIds.length})` : ''}
        </button>

        <div ref={projectRef} className="relative">
          <button
            type="button"
            className={`rounded-full border px-2.5 py-1 text-xs ${
              projectActive
                ? 'border-tg-accent/50 bg-tg-accent-soft text-tg-accent'
                : 'border-tg-border-soft bg-tg-surface text-tg-text-secondary'
            }`}
            onClick={() => {
              setProjectOpen((value) => !value);
              setPriorityOpen(false);
              setTagsOpen(false);
            }}
            aria-expanded={projectOpen}
            aria-haspopup="menu"
          >
            {projectLabel}
            <span aria-hidden="true" className="ml-1 opacity-70">
              ▾
            </span>
          </button>
          <div className={menuClass(projectOpen)} role="menu" aria-label="Фильтр по проекту">
            <button
              type="button"
              role="menuitemradio"
              aria-checked={filters.projectId === null}
              className={itemClass(filters.projectId === null)}
              onClick={() => {
                onChange({ ...filters, projectId: null });
                setProjectOpen(false);
              }}
            >
              Все проекты
            </button>
            {allProjects.map((project) => (
              <button
                key={project.id}
                type="button"
                role="menuitemradio"
                aria-checked={filters.projectId === project.id}
                className={itemClass(filters.projectId === project.id)}
                onClick={() => {
                  onChange({ ...filters, projectId: project.id });
                  setProjectOpen(false);
                }}
              >
                <span
                  className="mr-1.5 inline-block h-2 w-2 rounded-full align-middle"
                  style={{ backgroundColor: project.color }}
                  aria-hidden="true"
                />
                {project.name}
              </button>
            ))}
            {allProjects.length === 0 ? (
              <p className="px-2.5 py-2 text-xs text-tg-text-muted">Проектов пока нет</p>
            ) : null}
          </div>
        </div>

        <div ref={priorityRef} className="relative">
          <button
            type="button"
            className="rounded-full border border-tg-border-soft bg-tg-surface px-2.5 py-1 text-xs text-tg-text-secondary"
            onClick={() => {
              setPriorityOpen((value) => !value);
              setProjectOpen(false);
              setTagsOpen(false);
            }}
            aria-expanded={priorityOpen}
            aria-haspopup="menu"
          >
            {selectedPriorityLabel}
            <span aria-hidden="true" className="ml-1 opacity-70">
              ▾
            </span>
          </button>
          <div className={menuClass(priorityOpen)} role="menu" aria-label="Фильтр по приоритету">
            {PRIORITY_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                role="menuitemradio"
                aria-checked={filters.priority === option.value}
                className={itemClass(filters.priority === option.value)}
                onClick={() => {
                  onChange({ ...filters, priority: option.value });
                  setPriorityOpen(false);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <label className="inline-flex cursor-pointer items-center gap-2 text-xs text-tg-text-secondary">
          <input
            type="checkbox"
            className="h-4 w-4 accent-tg-accent"
            checked={filters.hideCompleted}
            onChange={(event) => {
              onChange({ ...filters, hideCompleted: event.target.checked });
            }}
          />
          Скрыть выполненные
        </label>

        {active ? (
          <button
            type="button"
            className="text-xs text-tg-accent hover:text-tg-accent-hover"
            onClick={() => {
              onChange({ ...EMPTY_TASK_FILTERS, hideCompleted: filters.hideCompleted });
              setProjectOpen(false);
              setPriorityOpen(false);
              setTagsOpen(false);
            }}
          >
            Сбросить фильтры
          </button>
        ) : null}
      </div>

      {tagsOpen ? (
        <div className="flex flex-wrap gap-1.5 rounded-xl border border-tg-border-soft bg-tg-bg p-2">
          {allTags.length === 0 ? (
            <p className="text-xs text-tg-text-muted">Тегов пока нет — создайте их в редакторе задачи</p>
          ) : (
            allTags.map((tag) => {
              const selected = filters.tagIds.includes(tag.id);
              return (
                <button
                  key={tag.id}
                  type="button"
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${
                    selected ? 'ring-1 ring-tg-accent/60' : ''
                  }`}
                  style={{
                    backgroundColor: `${tag.color}22`,
                    borderColor: `${tag.color}70`,
                    color: tag.color,
                  }}
                  onClick={() => {
                    toggleTag(tag.id);
                  }}
                >
                  {selected ? '✓ ' : ''}
                  {tag.name}
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}
